import callConstants from "constants/Call";

const STATUS = callConstants.TRANSCRIPT_STATUSES;
const REASONS = callConstants.TRANSCRIPT_SKIP_REASONS;
const OUTCOMES = callConstants.TRANSCRIPT_REQUEST_OUTCOMES;
const IN_PROGRESS = new Set(callConstants.TRANSCRIPT_STATUSES_IN_PROGRESS);

export const IN_PROGRESS_MESSAGE = "Transcription in progress…";
export const TOO_SHORT_MESSAGE = "This recording was too short to transcribe.";
export const NOT_REACHED_MESSAGE =
  "This call was not transcribed because it did not reach anyone.";
export const SKIPPED_MESSAGE = "This recording was not transcribed.";
export const FAILED_MESSAGE = "This recording could not be transcribed.";
export const NOT_REQUESTED_MESSAGE =
  "Transcription was not requested for this call.";
export const NO_RECORDING_MESSAGE = "This call has no recording.";
export const NOT_DISPOSITIONED_MESSAGE = "This call has no disposition yet.";
export const NOT_FOUND_MESSAGE = "This call is no longer available.";
export const ALREADY_TRANSCRIBED_MESSAGE =
  "This call already has a transcript.";
export const PREVIOUS_ATTEMPT_FAILED_MESSAGE =
  "A previous transcription attempt failed.";

const SKIP_MESSAGES = {
  [REASONS.TOO_SHORT]: TOO_SHORT_MESSAGE,
  [REASONS.NOT_REACHED]: NOT_REACHED_MESSAGE,
};

// Outcomes whose wording needs nothing from the transcript. The rest are
// resolved in getTranscriptRequestFailureMessage.
const REQUEST_FAILURE_MESSAGES = {
  [OUTCOMES.NOT_REACHED]: NOT_REACHED_MESSAGE,
  [OUTCOMES.NO_RECORDING]: NO_RECORDING_MESSAGE,
  [OUTCOMES.NOT_DISPOSITIONED]: NOT_DISPOSITIONED_MESSAGE,
  [OUTCOMES.NOT_FOUND]: NOT_FOUND_MESSAGE,
};

/**
 * Why a call's recording transcript cannot be read, or null when it can.
 *
 * A skipped transcript written before the reason was recorded, or one carrying
 * a reason this build does not know, falls back to the general wording rather
 * than leaving the user with an empty tooltip.
 */
export function getTranscriptUnavailableMessage({
  transcriptStatus,
  transcriptSkipReason,
  hasRecording,
}) {
  if (!transcriptStatus) {
    return hasRecording ? NOT_REQUESTED_MESSAGE : NO_RECORDING_MESSAGE;
  }

  if (IN_PROGRESS.has(transcriptStatus)) {
    return IN_PROGRESS_MESSAGE;
  }

  if (transcriptStatus === STATUS.SKIPPED) {
    return SKIP_MESSAGES[transcriptSkipReason] ?? SKIPPED_MESSAGE;
  }

  if (transcriptStatus === STATUS.FAILED) {
    return FAILED_MESSAGE;
  }

  return null;
}

/**
 * Why a manual transcription request did not queue a call.
 *
 * An already-requested call is told apart by the transcript's own status, so
 * the server returns one code for all three rather than splitting it.
 * ``minimumDurationSeconds`` comes back with the response because the
 * too-short wording quotes a value only the server knows.
 */
export function getTranscriptRequestFailureMessage({
  outcome,
  transcriptStatus,
  minimumDurationSeconds,
}) {
  if (outcome === OUTCOMES.TOO_SHORT) {
    return minimumDurationSeconds
      ? `Recording is shorter than ${minimumDurationSeconds} seconds.`
      : TOO_SHORT_MESSAGE;
  }

  if (outcome === OUTCOMES.ALREADY_REQUESTED) {
    if (IN_PROGRESS.has(transcriptStatus)) {
      return IN_PROGRESS_MESSAGE;
    }
    return transcriptStatus === STATUS.FAILED
      ? PREVIOUS_ATTEMPT_FAILED_MESSAGE
      : ALREADY_TRANSCRIBED_MESSAGE;
  }

  return REQUEST_FAILURE_MESSAGES[outcome] ?? SKIPPED_MESSAGE;
}
